import { ArrowLeft, Download, Presentation } from "lucide-react";

import { Button } from "@/components/ui/button";
import type { GenerationResult } from "@/lib/api";

import { AddToLibrary } from "./add-to-library";
import { CopyDraftPanel } from "./copy-draft";
import { LANGUAGE_NAMES, versionSummary } from "./utils";

/**
 * The frame every opened element sits in.
 *
 * One back button, in one place, on every element view — the way out of a
 * detail screen should never move depending on what you opened.
 */
export function ElementFrame({
  eyebrow,
  title,
  onBack,
  action,
  children,
}: {
  eyebrow: string;
  title: string;
  onBack: () => void;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" onClick={onBack} className="-ml-2">
        <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
        Back to the conversation
      </Button>

      <header className="space-y-2">
        <p className="type-label text-muted">{eyebrow}</p>
        <div className="flex flex-wrap items-start gap-3">
          <h2 className="font-heading text-foreground flex-1 text-2xl font-bold break-words">
            {title}
          </h2>
          {action}
        </div>
      </header>

      {children}
    </div>
  );
}

/** One generated artifact: what it is, what went into it, and how to take it. */
export function FileView({ result, onBack }: { result: GenerationResult; onBack: () => void }) {
  const summary = result.translation_summary;
  const flagged = summary.strings_needing_review ?? 0;
  const target = result.spec.target_language;
  const language = (target && LANGUAGE_NAMES[target]) || target || "—";

  return (
    <ElementFrame
      eyebrow="File"
      title={result.filename}
      onBack={onBack}
      action={
        <div className="flex items-center gap-2">
          <AddToLibrary result={result} />
          {result.download_url && (
            <Button asChild>
              <a href={result.download_url} download>
                <Download className="mr-1.5 h-3.5 w-3.5" />
                Download .{result.output_kind}
              </a>
            </Button>
          )}
        </div>
      }
    >
      <dl className="border-border bg-card grid grid-cols-2 gap-x-6 gap-y-3 rounded-lg border p-4 text-sm sm:grid-cols-3">
        {/* Only claimed when there was one: a rendered ad set is drawn from the
            brand kit, not stamped out of a deck template, and naming the VELUX
            template anyway would be describing a file that does not exist. */}
        {result.template_used && <Fact label="Built on" value={result.template_used} />}
        {/* The same description the rail and the element list use, so one file
            does not read as "12 slides" in one place and "0 pages kept" in
            another. */}
        <Fact label="Contains" value={versionSummary(result)} />
        <Fact label="Language" value={language} />
        <Fact label="Audience" value={result.spec.audience ?? "—"} />
        <Fact label="Strings translated" value={String(summary.strings_translated ?? 0)} />
        <Fact
          label="Needing review"
          value={String(flagged)}
          tone={flagged > 0 ? "escalated" : undefined}
        />
      </dl>

      {/* Written copy is the one artifact whose deliverable is not the file.
          Null for every other work mode, so a deck or a localized document
          reaches this view exactly as it did before. */}
      {result.copy_draft && <CopyDraftPanel draft={result.copy_draft} context="file" />}

      {result.output_kind === "pptx" && (result.slides ?? []).length > 0 && (
        <section className="space-y-2">
          <h3 className="type-label text-muted">Slides</h3>
          <ol className="space-y-1.5">
            {(result.slides ?? []).map((slide) => (
              <li
                key={slide.index}
                className="border-border bg-card flex items-start gap-3 rounded-md border px-3 py-2"
              >
                <span className="text-muted mt-0.5 w-4 shrink-0 text-right text-xs tabular-nums">
                  {slide.index}
                </span>
                <Presentation className="text-faint mt-0.5 h-3.5 w-3.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-foreground text-sm break-words">
                    {slide.title ?? <span className="text-muted italic">no title</span>}
                  </p>
                  <p className="text-muted mt-0.5 truncate text-[11px]">{slide.layout}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>
      )}
    </ElementFrame>
  );
}

function Fact({ label, value, tone }: { label: string; value: string; tone?: "escalated" }) {
  return (
    <div>
      <dt className="text-muted text-[11px] tracking-wide uppercase">{label}</dt>
      <dd
        className={
          tone === "escalated"
            ? "text-outcome-escalated font-semibold"
            : "text-foreground font-medium"
        }
      >
        {value}
      </dd>
    </div>
  );
}
